import React, { useState, useMemo } from "react";
import { Button, Input, Modal, Select, Space, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { RejectionReason, AutopodderResultRow, ScheduleDataItem } from "./types";
import { getStartingUnits } from "./utils/slotTableUtils";

interface RejectionReasonsModalProps {
    open: boolean;
    onClose: () => void;
    selectedSlot: AutopodderResultRow | null;
    scheduleData: { [key: string]: ScheduleDataItem };
    getCurrentUnitsAvailable: (adName: string) => number;
}

export const RejectionReasonsModal: React.FC<RejectionReasonsModalProps> = ({
    open,
    onClose,
    selectedSlot,
    scheduleData,
    getCurrentUnitsAvailable,
}) => {
    const [rejectionSearchText, setRejectionSearchText] = useState<string>('');
    const [rejectionFilterNotUtilized, setRejectionFilterNotUtilized] = useState<boolean>(false);
    const [rejectionFilterLength, setRejectionFilterLength] = useState<number | undefined>(undefined);
    const [rejectionFilterReason, setRejectionFilterReason] = useState<string | undefined>(undefined);
    const [rejectionFilterDaysAllowed, setRejectionFilterDaysAllowed] = useState<string | undefined>(undefined);

    // Reset filters when modal closes or slot changes
    React.useEffect(() => {
        if (!open) {
            setRejectionSearchText('');
            setRejectionFilterNotUtilized(false);
            setRejectionFilterLength(undefined);
            setRejectionFilterReason(undefined);
            setRejectionFilterDaysAllowed(undefined);
        }
    }, [open]);

    const getRejectionReasons = (record: AutopodderResultRow): RejectionReason[] => {
        if (!record.REJECTION_REASONS) {
            return [];
        }
        try {
            return JSON.parse(record.REJECTION_REASONS) as RejectionReason[];
        } catch (error) {
            console.error('Error parsing rejection reasons:', error);
            return [];
        }
    };

    const getFilteredRejectionReasons = (record: AutopodderResultRow): RejectionReason[] => {
        let filteredReasons = getRejectionReasons(record);
        
        // Filter by "not 100% utilized" - based on CURRENT units available, not historical
        if (rejectionFilterNotUtilized) {
            filteredReasons = filteredReasons.filter(rejection => {
                const currentUnitsAvailable = getCurrentUnitsAvailable(rejection.adName);
                return currentUnitsAvailable > 0;
            });
        }
        
        // Filter by ad length
        if (rejectionFilterLength !== undefined) {
            filteredReasons = filteredReasons.filter(rejection => {
                const scheduleItem = scheduleData[rejection.adName];
                return scheduleItem && scheduleItem.UNITLENGTH === rejectionFilterLength;
            });
        }
        
        // Filter by rejection reason
        if (rejectionFilterReason) {
            filteredReasons = filteredReasons.filter(rejection => 
                rejection.reason === rejectionFilterReason
            );
        }
        
        // Filter by days allowed - case-insensitive search for day of week in DAYS_ALLOWED string
        if (rejectionFilterDaysAllowed) {
            filteredReasons = filteredReasons.filter(rejection => {
                const scheduleItem = scheduleData[rejection.adName];
                if (!scheduleItem || !scheduleItem.DAYS_ALLOWED) {
                    return false;
                }
                const daysAllowedLower = scheduleItem.DAYS_ALLOWED.toLowerCase();
                const selectedDayLower = rejectionFilterDaysAllowed.toLowerCase();
                // Check if the selected day appears in the DAYS_ALLOWED string
                // Match full day name or common abbreviations (e.g., "monday", "mon", "tuesday", "tue")
                const dayPatterns: { [key: string]: string[] } = {
                    'monday': ['monday', 'mon'],
                    'tuesday': ['tuesday', 'tue', 'tues'],
                    'wednesday': ['wednesday', 'wed'],
                    'thursday': ['thursday', 'thu', 'thur', 'thurs'],
                    'friday': ['friday', 'fri'],
                    'saturday': ['saturday', 'sat'],
                    'sunday': ['sunday', 'sun']
                };
                const patterns = dayPatterns[selectedDayLower] || [selectedDayLower];
                return patterns.some(pattern => daysAllowedLower.includes(pattern));
            });
        }
        
        // Filter by search text - case-insensitive search in ad name, reason, details, advertiser name, and brand
        if (rejectionSearchText) {
            const searchLower = rejectionSearchText.toLowerCase();
            filteredReasons = filteredReasons.filter(rejection => {
                const matchesAdName = rejection.adName.toLowerCase().includes(searchLower);
                const matchesReason = rejection.reason.toLowerCase().includes(searchLower);
                const matchesDetails = rejection.details && rejection.details.toLowerCase().includes(searchLower);
                
                // Check advertiser name and brand from schedule data
                const scheduleItem = scheduleData[rejection.adName];
                const matchesAdvertiserName = scheduleItem?.ADVERTISER_NAME && 
                    scheduleItem.ADVERTISER_NAME.toLowerCase().includes(searchLower);
                const matchesBrand = scheduleItem?.DEALBRAND && 
                    scheduleItem.DEALBRAND.toLowerCase().includes(searchLower);
                
                return matchesAdName || matchesReason || matchesDetails || matchesAdvertiserName || matchesBrand;
            });
        }
        
        return filteredReasons;
    };

    const getUniqueRejectionAdLengths = (): { label: string; value: number; count: number }[] => {
        if (!selectedSlot) return [];
        
        const rejectionReasons = getRejectionReasons(selectedSlot);
        const lengthCounts = new Map<number, number>();
        
        rejectionReasons.forEach(rejection => {
            const scheduleItem = scheduleData[rejection.adName];
            if (scheduleItem && scheduleItem.UNITLENGTH !== undefined && scheduleItem.UNITLENGTH !== null) {
                const length = scheduleItem.UNITLENGTH;
                lengthCounts.set(length, (lengthCounts.get(length) || 0) + 1);
            }
        });
        
        return Array.from(lengthCounts.entries())
            .map(([length, count]) => ({
                label: `${length}s`,
                value: length,
                count: count
            }))
            .sort((a, b) => a.value - b.value);
    };

    const getUniqueRejectionReasons = (): { label: string; value: string; count: number }[] => {
        if (!selectedSlot) return [];
        
        const rejectionReasons = getRejectionReasons(selectedSlot);
        const reasonCounts = new Map<string, number>();
        
        rejectionReasons.forEach(rejection => {
            const reason = rejection.reason;
            reasonCounts.set(reason, (reasonCounts.get(reason) || 0) + 1);
        });
        
        return Array.from(reasonCounts.entries())
            .map(([reason, count]) => ({
                label: reason,
                value: reason,
                count: count
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    };

    const getUniqueRejectionDaysAllowed = (): { label: string; value: string; count: number }[] => {
        if (!selectedSlot) return [];
        
        const rejectionReasons = getRejectionReasons(selectedSlot);
        const dayCounts = new Map<string, number>();
        
        // Days of the week with their common abbreviations
        const daysOfWeek: { full: string; patterns: string[] }[] = [
            { full: 'Monday', patterns: ['monday', 'mon'] },
            { full: 'Tuesday', patterns: ['tuesday', 'tue', 'tues'] },
            { full: 'Wednesday', patterns: ['wednesday', 'wed'] },
            { full: 'Thursday', patterns: ['thursday', 'thu', 'thur', 'thurs'] },
            { full: 'Friday', patterns: ['friday', 'fri'] },
            { full: 'Saturday', patterns: ['saturday', 'sat'] },
            { full: 'Sunday', patterns: ['sunday', 'sun'] }
        ];
        
        // Count how many rejection reasons contain each day
        daysOfWeek.forEach(day => {
            let count = 0;
            rejectionReasons.forEach(rejection => {
                const scheduleItem = scheduleData[rejection.adName];
                if (scheduleItem && scheduleItem.DAYS_ALLOWED) {
                    const daysAllowedLower = scheduleItem.DAYS_ALLOWED.toLowerCase();
                    // Check if any pattern for this day appears in DAYS_ALLOWED
                    if (day.patterns.some(pattern => daysAllowedLower.includes(pattern))) {
                        count++;
                    }
                }
            });
            if (count > 0) {
                dayCounts.set(day.full, count);
            }
        });
        
        // Return days in order (Monday through Sunday)
        return daysOfWeek
            .filter(day => dayCounts.has(day.full))
            .map(day => ({
                label: day.full,
                value: day.full,
                count: dayCounts.get(day.full) || 0
            }));
    };

    const uniqueAdLengths = useMemo(() => getUniqueRejectionAdLengths(), [selectedSlot, scheduleData]);
    const uniqueRejectionReasons = useMemo(() => getUniqueRejectionReasons(), [selectedSlot]);
    const uniqueDaysAllowed = useMemo(() => getUniqueRejectionDaysAllowed(), [selectedSlot, scheduleData]);

    const handleClearFilters = () => {
        setRejectionFilterNotUtilized(false);
        setRejectionSearchText('');
        setRejectionFilterLength(undefined);
        setRejectionFilterReason(undefined);
        setRejectionFilterDaysAllowed(undefined);
    };

    if (!selectedSlot) {
        return null;
    }

    const filteredReasons = getFilteredRejectionReasons(selectedSlot);
    const allReasons = getRejectionReasons(selectedSlot);
    const notUtilizedCount = allReasons.filter(r => {
        const currentUnitsAvailable = getCurrentUnitsAvailable(r.adName);
        return currentUnitsAvailable > 0;
    }).length;

    return (
        <Modal
            title={`Rejection Reasons - Slot: ${selectedSlot.SLOT_ID || ''}`}
            open={open}
            onCancel={onClose}
            footer={[
                <Button key="close" onClick={onClose}>
                    Close
                </Button>
            ]}
            width={800}
        >
            <div>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Input
                        placeholder="Search rejection reasons (ad name, reason, details, advertiser, brand)"
                        value={rejectionSearchText}
                        onChange={(e) => setRejectionSearchText(e.target.value)}
                        allowClear
                        prefix={<SearchOutlined />}
                    />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontWeight: 500, marginRight: 8 }}>Filters:</span>
                        <Button
                            type={rejectionFilterNotUtilized ? 'primary' : 'default'}
                            size="small"
                            onClick={() => setRejectionFilterNotUtilized(!rejectionFilterNotUtilized)}
                        >
                            Not 100% Utilized
                            {notUtilizedCount > 0 ? ` (${notUtilizedCount})` : ''}
                        </Button>
                        <Select
                            placeholder="Filter by Ad Length"
                            value={rejectionFilterLength}
                            onChange={setRejectionFilterLength}
                            allowClear
                            style={{ width: 150 }}
                            size="small"
                        >
                            {uniqueAdLengths.map(length => (
                                <Select.Option 
                                    key={length.value} 
                                    value={length.value}
                                >
                                    {length.label} ({length.count})
                                </Select.Option>
                            ))}
                        </Select>
                        <Select
                            placeholder="Filter by Reason"
                            value={rejectionFilterReason}
                            onChange={setRejectionFilterReason}
                            allowClear
                            style={{ width: 200 }}
                            size="small"
                        >
                            {uniqueRejectionReasons.map(reason => (
                                <Select.Option 
                                    key={reason.value} 
                                    value={reason.value}
                                >
                                    {reason.label} ({reason.count})
                                </Select.Option>
                            ))}
                        </Select>
                        <Select
                            placeholder="Filter by Days Allowed"
                            value={rejectionFilterDaysAllowed}
                            onChange={setRejectionFilterDaysAllowed}
                            allowClear
                            style={{ width: 180 }}
                            size="small"
                        >
                            {uniqueDaysAllowed.map(daysAllowed => (
                                <Select.Option 
                                    key={daysAllowed.value} 
                                    value={daysAllowed.value}
                                >
                                    {daysAllowed.label} ({daysAllowed.count})
                                </Select.Option>
                            ))}
                        </Select>
                        {(rejectionFilterNotUtilized || rejectionSearchText || rejectionFilterLength !== undefined || rejectionFilterReason !== undefined || rejectionFilterDaysAllowed !== undefined) && (
                            <Button
                                type="link"
                                size="small"
                                onClick={handleClearFilters}
                            >
                                Clear Filters
                            </Button>
                        )}
                    </div>
                </Space>
                <div style={{ maxHeight: '60vh', overflowY: 'auto', marginTop: 16 }}>
                    {filteredReasons.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                            {rejectionSearchText || rejectionFilterNotUtilized || rejectionFilterLength !== undefined || rejectionFilterReason !== undefined || rejectionFilterDaysAllowed !== undefined
                                ? 'No matching rejection reasons found' 
                                : 'No rejection reasons for this slot'}
                        </div>
                    ) : (
                        filteredReasons.map((rejection, index) => {
                            const currentUnitsAvailable = getCurrentUnitsAvailable(rejection.adName);
                            const scheduleItem = scheduleData[rejection.adName];
                            const isNotUtilized = currentUnitsAvailable > 0;
                            
                            return (
                                <div 
                                    key={index}
                                    style={{ 
                                        marginBottom: 12,
                                        padding: 12,
                                        backgroundColor: '#fff7e6',
                                        border: '1px solid #ffd591',
                                        borderRadius: 4,
                                        fontSize: '13px',
                                        lineHeight: '1.6'
                                    }}
                                >
                                    <div style={{ fontWeight: 600, color: '#d46b08', marginBottom: 6, fontSize: '14px' }}>
                                        {rejection.adName}
                                    </div>
                                    {scheduleItem && (
                                        <div style={{ marginBottom: 8, fontSize: '12px' }}>
                                            <div style={{ display: 'flex', gap: 16, marginBottom: 4, flexWrap: 'wrap' }}>
                                                {scheduleItem.ADVERTISER_NAME && (
                                                    <div style={{ color: '#666' }}>
                                                        <strong>Advertiser:</strong> {scheduleItem.ADVERTISER_NAME}
                                                    </div>
                                                )}
                                                {scheduleItem.ADVERTISER_ID && (
                                                    <div style={{ color: '#666' }}>
                                                        <strong>Advertiser ID:</strong> {scheduleItem.ADVERTISER_ID}
                                                    </div>
                                                )}
                                                {scheduleItem.DEALBRAND && (
                                                    <div style={{ color: '#666' }}>
                                                        <strong>Brand:</strong> {scheduleItem.DEALBRAND}
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: 16, marginBottom: 4, flexWrap: 'wrap' }}>
                                                {scheduleItem.DAYS_ALLOWED && (
                                                    <div style={{ color: '#666' }}>
                                                        <strong>Days Allowed:</strong> {scheduleItem.DAYS_ALLOWED}
                                                    </div>
                                                )}
                                                {scheduleItem.UNITLENGTH !== undefined && scheduleItem.UNITLENGTH !== null && (
                                                    <div style={{ color: '#666' }}>
                                                        <strong>Length:</strong> {scheduleItem.UNITLENGTH}s
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ color: '#666', marginBottom: 4 }}>
                                        <strong>Reason:</strong> {rejection.reason}
                                    </div>
                                    <div style={{ color: '#333', marginTop: 6, fontFamily: 'monospace', fontSize: '12px', backgroundColor: '#fafafa', padding: 8, borderRadius: 2 }}>
                                        {rejection.details}
                                    </div>
                                    <div style={{ marginTop: 8 }}>
                                        <div style={{ 
                                            color: isNotUtilized ? '#ff4d4f' : '#666', 
                                            fontSize: '12px',
                                            fontWeight: isNotUtilized ? 600 : 'normal',
                                            marginBottom: 4
                                        }}>
                                            <strong>Current Units Available:</strong> {currentUnitsAvailable}
                                            {scheduleItem && getStartingUnits(scheduleItem) > 0 && (() => {
                                                const startingUnits = getStartingUnits(scheduleItem);
                                                return (
                                                    <span style={{ marginLeft: 8, color: '#666', fontWeight: 'normal' }}>
                                                        / {startingUnits} (Utilization: {((startingUnits - currentUnitsAvailable) / startingUnits * 100).toFixed(1)}%)
                                                    </span>
                                                );
                                            })()}
                                            {isNotUtilized && (
                                                <Tag color="red" style={{ marginLeft: 8 }}>
                                                    Not 100% Utilized
                                                </Tag>
                                            )}
                                        </div>
                                        {rejection.unitsAvailable !== undefined && rejection.unitsAvailable !== currentUnitsAvailable && (
                                            <div style={{ 
                                                color: '#999', 
                                                fontSize: '11px',
                                                fontStyle: 'italic'
                                            }}>
                                                Units available at rejection time: {rejection.unitsAvailable}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </Modal>
    );
};
